import React, { useState, useEffect } from 'react';
import {
  Layout,
  List,
  Input,
  Typography,
  Spin,
  message,
  Button
} from 'antd';
import {
  StarOutlined,
  StarFilled,
  DeleteOutlined,EditOutlined
} from '@ant-design/icons';
import axios from 'axios';
import './NoteStyles.css';
import NewNoteModal from './NewNoteModal';
import API from '../../API/API';
import Config from '../../API/Config';
import { useNavigate } from 'react-router-dom';
const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

const NoteList = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [isNewNoteOpen, setIsNewNoteOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotes = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
        
      const response = await axios.get(
        `${Config.baseURL}/notes/agent/${JSON.parse(localStorage.getItem("userData"))?.id}`,
        {
          params: {
            page,
            limit: pageSize
          }
        }
      );

      if (response.data.status) {
        setNotes(response.data.data);
        setPagination({
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize,
          total: response.data.pagination.totalItems
        });

        if (response.data.data.length > 0) {
          setSelectedNote(response.data.data[0]);
        }
      } else {
        message.error('Failed to fetch notes');
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
      message.error('Error fetching notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  const filteredNotes = notes.filter(note =>
    (note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (activeSection === 'all' || (activeSection === 'starred' && note.starred))
  );

  const toggleStar = async (id) => {
    try {
      const updatedNotes = notes.map(note =>
        note._id === id ? { ...note, starred: !note.starred } : note
      );
      setNotes(updatedNotes);

      await axios.put(`${Config.baseURL}/notes/${id}`, {
        starred: !notes.find(note => note._id === id).starred
      });
    } catch (error) {
      console.error('Error updating note:', error);
      message.error('Failed to update note');
      fetchNotes(pagination.current, pagination.pageSize);
    }
  };

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${Config.baseURL}/notes/${id}`);
      message.success('Note deleted successfully');

      const updatedNotes = notes.filter(note => note._id !== id);
      setNotes(updatedNotes);
      setSelectedNote(updatedNotes.length > 0 ? updatedNotes[0] : null);
    } catch (error) {
      console.error('Error deleting note:', error);
      message.error('Failed to delete note');
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setEditedTitle(selectedNote.title);
    setEditedContent(selectedNote.content);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const saveChanges = async () => {
    try {
      const response = await axios.put(`${Config.baseURL}/notes/${selectedNote._id}`, {
        title: editedTitle,
        content: editedContent
      });

      if (response.data.status) {
        const updatedNotes = notes.map(note =>
          note._id === selectedNote._id
            ? { ...note, title: editedTitle, content: editedContent }
            : note
        );
        setNotes(updatedNotes);
        setSelectedNote({ ...selectedNote, title: editedTitle, content: editedContent });
        setIsEditing(false);
        message.success('Note updated successfully');
      } else {
        message.error('Failed to update note');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      message.error('Failed to save changes');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <Layout className="note-app-layout">
      <Sider width={250} className="note-sidebar">
        <div className="sidebar-header">
            <Button 
        type="text" 
        onClick={() => navigate(-1)} 
        style={{ marginRight: 16 }}
      >
        <span style={{ display: 'flex', alignItems: 'center',color:'#1890ff' }}>
          &lt; Back /
        </span>
      </Button> <Title level={3} className="app-title">Notes</Title>
        </div>

        <Button type="primary" className="new-note-button"  onClick={() => setIsNewNoteOpen(true)}>New Note</Button>

        <div className="sidebar-sections">
          <div
            className={`sidebar-section ${activeSection === 'all' ? 'active' : ''}`}
            onClick={() => setActiveSection('all')}
          >
            <Text className="section-title">All Notes</Text>
          </div>
          <div
            className={`sidebar-section ${activeSection === 'starred' ? 'active' : ''}`}
            onClick={() => setActiveSection('starred')}
          >
            <Text className="section-title">Starred</Text>
          </div>
        </div>
      </Sider>

      <Sider width={320} className="note-sidebar">
        <Search
          placeholder="Search notes..."
          className="note-search"
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />

        <Spin spinning={loading}>
          <List
            dataSource={filteredNotes}
            className="note-list"
            renderItem={(note) => (
              <List.Item
                className={`note-item ${selectedNote?._id === note._id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedNote(note);
                  setIsEditing(false); // Exit edit mode on new selection
                }}
              >
                <div className="note-content">
                  <div className="note-header">
                    <Title level={5} className="note-title">{note.title}</Title>
                    <span
                      className="star-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStar(note._id);
                      }}
                    >
                      {note.starred
                        ? <StarFilled style={{ color: '#FFD700' }} />
                        : <StarOutlined />}
                    </span>
                  </div>
                  <Text ellipsis className="note-preview">
                    {note.content}
                  </Text>
                  <Text type="secondary" className="note-date">
                    {formatDate(note.createdAt)}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        </Spin>
      </Sider>

      <Layout className="note-main-layout">
        <Header className="note-detail-header">
          <div className="note-detail-header-content">
            <Title level={3} className="note-detail-title">
              {isEditing ? (
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="edit-title-input"
                />
              ) : (
                selectedNote?.title || 'Select a note'
              )}
            </Title>
            {selectedNote && (
              <div className="note-actions">
                {!isEditing ? (
                  <>
                    <Button
                      type="text"
                      icon={selectedNote.starred ? <StarFilled style={{ color: '#FFD700' }} /> : <StarOutlined />}
                      onClick={() => toggleStar(selectedNote._id)}
                    />
                    <Button type="text" onClick={startEditing}><EditOutlined /></Button>
                    <Button type="text" danger onClick={() => deleteNote(selectedNote._id)}>
                      <DeleteOutlined />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="primary" onClick={saveChanges}>Save</Button>
                    <Button onClick={cancelEditing}>Cancel</Button>
                  </>
                )}
              </div>
            )}
          </div>
        </Header>

        <Content className="note-detail-content">
          {selectedNote ? (
            isEditing ? (
              <Input.TextArea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={12}
                className="edit-content-textarea"
              />
            ) : (
              <>
                <Text type="secondary" className="last-edited">
                  Last edited: {formatDate(selectedNote.updatedAt)}
                </Text>
                <Text className="note-full-content">{selectedNote.content}</Text>
              </>
            )
          ) : (
            <Text type="secondary" className="empty-state">
              Select a note to view its content
            </Text>
          )}
        </Content>
      </Layout>
      <NewNoteModal
  visible={isNewNoteOpen}
  onClose={() => setIsNewNoteOpen(false)}
  onNoteAdded={() => fetchNotes(pagination.current, pagination.pageSize)}
/>
    </Layout>
    
  );
};

export default NoteList;
